/**
 * @file     cardCta.ts
 * @readme   ./CLAUDE.md
 * @purpose  One registry deciding what a listing card offers, per surface.
 *
 *           The gap this closes: page-level capture exists on nearly every
 *           listing surface, but the cards themselves offered only a like.
 *           The card is where attention is and where the specific intent
 *           lives, and a visitor on an open-house page wants something
 *           different from one on a price-drop page.
 *
 *           A registry rather than per-page props, because there are twelve
 *           surfaces and several card components: hand-placing means the next
 *           surface inherits nothing, silently, which is how this gap opened.
 */
import type { ShortlistAlertEvent } from '@/features/seo/shortlistIntent';

/** Every surface that renders listing cards from a cold entry point. */
export type CardCtaSurface =
  | 'open-houses'
  | 'price-drops'
  | 'new-listings'
  | 'hot-deals'
  | 'buildings'
  | 'neighbourhood'
  | 'facets'
  | 'homes-city'
  | 'landing'
  | 'market-report'
  | 'british-columbia'
  | 'rent-vs-mortgage'
  | 'sell-house'
  | 'presales';

export type CardCtaKind =
  /** Opens the alert flow, pre-set to this surface's event. */
  | { kind: 'alert'; event: ShortlistAlertEvent; label: string }
  /**
   * Opens LeadCaptureModal for the clicked listing via CardCtaProvider.
   * Tracking alone is not a click handler. Inquiry is listing-scoped, so
   * completing one card must not disable the rest of the grid.
   */
  | { kind: 'inquiry'; label: string }
  /** Plain link, for surfaces whose next step is a page rather than a form. */
  | { kind: 'link'; label: string; href: (city: string, slug: string) => string };

/**
 * Copy rule: every label is inquiry-shaped or self-serve. None implies
 * representation, because no agency relationship exists before a signed
 * service agreement (voice-and-tone.md §7). "Ask about this" is a question;
 * "Book a showing with your agent" is a claim. Only the first is allowed.
 */
export const CARD_CTA: Record<CardCtaSurface, CardCtaKind> = {
  // An open house needs no appointment, so a showing request would add
  // friction to the one path that has none. A reminder is the useful act,
  // and attending is a stronger intent signal than a booking request.
  'open-houses': { kind: 'alert', event: 'open_house', label: 'Remind me' },
  'price-drops': { kind: 'alert', event: 'price_drop', label: 'Watch the price' },
  'new-listings': { kind: 'alert', event: 'new_listing', label: 'Alert me to similar' },
  'hot-deals': { kind: 'alert', event: 'price_drop', label: 'Watch the price' },
  buildings: { kind: 'alert', event: 'new_listing', label: 'Watch this building' },
  neighbourhood: { kind: 'alert', event: 'new_listing', label: 'Watch this area' },
  facets: { kind: 'alert', event: 'new_listing', label: 'Alert me to similar' },
  'homes-city': { kind: 'alert', event: 'new_listing', label: 'Watch this area' },
  landing: { kind: 'alert', event: 'new_listing', label: 'Alert me to similar' },
  'market-report': { kind: 'inquiry', label: 'Ask about this' },
  'british-columbia': { kind: 'alert', event: 'new_listing', label: 'Alert me to similar' },
  'rent-vs-mortgage': { kind: 'alert', event: 'new_listing', label: 'Watch homes in your budget' },
  // Seller surfaces: the visitor is weighing selling, not buying. Send them to
  // the valuation family rather than asking them to watch someone else's home.
  // A presale question is about the deposit schedule, the completion and the
  // developer, none of which an alert answers.
  presales: { kind: 'inquiry', label: 'Ask about this' },
  'sell-house': {
    kind: 'link',
    label: 'What is mine worth',
    href: (_city, slug) => `/what-is-my-home-worth/${slug}`,
  },
};

export function cardCtaFor(surface: CardCtaSurface | undefined): CardCtaKind | null {
  return surface ? (CARD_CTA[surface] ?? null) : null;
}

/**
 * Cards whose root element is an anchor cannot host the CTA inside it.
 * ListingModalLinks intercepts clicks in the capture phase on any
 * `a[data-listing-key]` and calls stopPropagation, so a button nested inside
 * that anchor never receives its own click: the detail modal opens instead.
 * Card components must render the CTA as a sibling of the anchor, not a child.
 * Enforced by tests/unit/cardCta.test.ts.
 */
export const CTA_MUST_SIT_OUTSIDE_ANCHOR = true;
